import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { useStableObject } from '@soundxyz/graphql-react-query/utils';
import { WithReferralAddress } from '@soundxyz/utils/constants/embeds';
import { formatCountdown } from '@components/Landing/sections/Hero/helpers';
import { useLikeRelease } from '@components/Likes/useLikeRelease';
import { SLogoSoundBarOverlay } from '@components/SoundBar/SLogoSoundBarOverlay';
import { AddToShelfMenuItem } from '@components/basics/Dropdowns/MenuItems/AddToShelfMenuItem';
import { LikeSoundMenuItem } from '@components/basics/Dropdowns/MenuItems/LikeSoundMenuItem';
import { PostReleaseMenuItem } from '@components/basics/Dropdowns/MenuItems/PostReleaseMenuItem';
import { ShareMenuItem } from '@components/basics/Dropdowns/MenuItems/ShareMenuItem';
import { ThreeDotsDropdown } from '@components/basics/Dropdowns/ThreeDotsDropdown';
import { PlayIndicatorBars } from '@components/main-components/cards/PlayIndicatorBars';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import { useAuth } from '@context/auth/base';
import { useMediaQuery } from '@context/mediaQuery';
import { type MintReferral, MintReferralType } from '@context/mintReferral';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, SoundBarReleaseFragmentDoc } from '@graphql/reactQuery';
import { useCheckReferrableRelease } from '@hooks/useCheckReferrable';
import { useGetDominantBackgroundColor } from '@hooks/useGetDominantBackgroundColor';
import { usePlayButton } from '@hooks/usePlayButton';
import { useTimer } from '@hooks/useTimer';
import { DEFAULT_DOMINANT_BACKGROUND_COLOR } from '@utils/constants';
import { getStaticCoverImage } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

export const ReleaseWidget = ({
  releaseFrag,
  creatorPublicAddress,
  postId,
}: {
  releaseFrag: FragmentType<SoundBarReleaseFragmentDoc>;
  postId: string;
  creatorPublicAddress: string;
}) => {
  const mintReferral = useStableObject<MintReferral>({
    referralType: MintReferralType.Post,
    referralData: {
      postId,
      postUserAddress: creatorPublicAddress,
    },
  });
  const { authUser } = useAuth();
  const { tablet } = useMediaQuery();
  const release = getFragment(SoundBarReleaseFragmentDoc, releaseFrag);

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const {
    isPlaying,
    isLoading: isPlayButtonLoading,
    onPlayClick,
    currentListeningParty,
  } = usePlayButton({
    isAlbum: false,
    trackId: release.track.id,
    listeningParty: release.listeningParty,
    releaseId: release.id,
  });

  const dominantColor =
    release.coverImage.dominantColor === '#FFFFFF' || !release.coverImage.dominantColor
      ? DEFAULT_DOMINANT_BACKGROUND_COLOR
      : release.coverImage.dominantColor;

  const dominantBackgroundColor = useGetDominantBackgroundColor({ dominantColor });

  const userOwnsGoldenEgg = useMemo(
    () => !!release.goldenEggNft && !!authUser && release.goldenEggNft.owner.id === authUser.id,
    [authUser, release.goldenEggNft],
  );

  const hasSaleStarted = useMemo(
    () => release.mintStartTimestamp <= Date.now(),
    [release.mintStartTimestamp],
  );

  const saleStartCountdown = useTimer({
    expiryTimestamp: release.mintStartTimestamp,
  });

  const formattedStartCountDown = useMemo(
    () => formatCountdown({ ...saleStartCountdown, prefix: 'Starts in' }),
    [saleStartCountdown],
  );

  const { isShareReferrable } = useCheckReferrableRelease({
    release,
  });

  const { isLiked, onClick: onLikeClick } = useLikeRelease({ id: release.id, source: 'Card' });

  const songPagePath =
    mintReferral && mintReferral.referralType === MintReferralType.Post && isShareReferrable
      ? webappUriPath(
          WithReferralAddress(release.webappUri, {
            referralAddress: mintReferral.referralData.postUserAddress,
            source: {
              postId: mintReferral.referralData.postId,
            },
          }),
        )
      : webappUriPath(release.webappUri);

  return (
    <div className="flex flex-col gap-2">
      <div
        className="align-center relative flex w-full items-center overflow-hidden rounded-lg p-4"
        style={{ backgroundColor: dominantBackgroundColor }}
      >
        <div className="absolute inset-0 left-0 top-0 w-full overflow-hidden bg-neutral900 opacity-40" />
        <div className="absolute left-[300px] top-0 overflow-hidden">
          <SLogoSoundBarOverlay className="relative overflow-hidden" />
        </div>

        <div className="color-white absolute right-4 top-4 z-above1 flex">
          <ThreeDotsDropdown
            isDropdownOpen={isDropdownOpen}
            setIsDropdownOpen={setIsDropdownOpen}
            variant="inverted"
            triggerProps={{ size: 24 }}
          >
            <PostReleaseMenuItem releaseFrag={release} source="Sound Bar" />
            <div className="w-full md:hidden">
              <LikeSoundMenuItem
                liked={isLiked}
                onClick={onLikeClick}
                releaseId={release.id}
                releaseType={release.type}
              />
            </div>
            <AddToShelfMenuItem releaseId={release.id} source="Collector Profile" />
            <ShareMenuItem releaseId={release.id} source="Collector Profile" />
          </ThreeDotsDropdown>
        </div>
        <div className="relative flex flex-1 items-center gap-4 overflow-hidden  md:items-stretch">
          <div className="relative aspect-square w-16 flex-00auto overflow-hidden rounded-md transition-opacity duration-300 ease-in-out md:rounded-lg">
            <NextFutureImage
              alt="featured release image"
              src={getStaticCoverImage({
                coverImage: release.coverImage.url,
                staticCoverImage: release.staticCoverImage?.url,
              })}
              fill
              sizes="64px"
            />
            <div
              className={clsx(
                'absolute inset-0',
                'items-center justify-center transition-opacity duration-300 ease-in-out md:opacity-0 md:hover:opacity-100',
                'z-above1 flex',
              )}
            >
              <PlayButton
                color="white"
                playing={isPlaying}
                onClick={onPlayClick}
                size={tablet ? 's' : 'xs'}
                loading={isPlayButtonLoading}
                currentListeningParty={currentListeningParty}
              />
            </div>
          </div>
          <div className="flex flex-1 flex-col justify-between overflow-hidden">
            <div className="flex flex-col gap-1 md:pt-1">
              <div className="overflow-hidden text-ellipsis whitespace-nowrap font-base text-[13px] leading-[18px] text-white">
                <AccessibleLink pathnameUri={webappUriPath(release.artist.webappUri)}>
                  {release.artist.name}
                </AccessibleLink>
              </div>
              <div className="flex w-full items-center gap-2 whitespace-nowrap font-title text-title-s font-medium text-white">
                {isPlaying && <PlayIndicatorBars smallBars={!tablet} />}
                <AccessibleLink
                  className="overflow-hidden text-ellipsis"
                  pathnameUri={songPagePath}
                >
                  {release.title}
                </AccessibleLink>
              </div>
              <div className="flex items-center gap-1">
                {userOwnsGoldenEgg && (
                  <NextFutureImage
                    alt="goldenEggBadge"
                    src="/images/golden-egg-badge.png"
                    className="h-4 w-4"
                    width={16}
                    height={16}
                  />
                )}
                <div className="flex gap-2 font-base text-base-xs leading-[18px] text-white opacity-60">
                  {!hasSaleStarted && <div className="">{formattedStartCountDown}</div>}
                  {!hasSaleStarted && <div className="">•</div>}
                  <div>{`${release.totalMinted} minted`}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
